/**
 * Unified Date/Time Handling for Singapore Timezone - MANDATORY USAGE
 *
 * ⚠️  CRITICAL: ALL date/time operations MUST use this unified system exclusively.
 * ⚠️  NEVER use native Date constructor, Date methods, or external date libraries.
 * ⚠️  This ensures consistent Singapore timezone (UTC+8) handling throughout the application.
 *
 * MANDATORY USAGE PATTERNS:
 * ✅ SingaporeTime.now()           - Get current Singapore timestamp
 * ✅ SingaporeTime.dateOnly()      - Get YYYY-MM-DD format
 * ✅ SingaporeTime.format()        - Display dates/times
 * ✅ SingaporeTime.dateRange()     - Database date filtering
 * ✅ SingaporeTime.isToday()       - Date comparisons
 *
 * ❌ new Date()                    - NEVER use native Date constructor
 * ❌ date.toISOString()            - NEVER use UTC methods
 * ❌ date.toLocaleDateString()     - NEVER use without unified system
 * ❌ moment(), dayjs(), date-fns   - NEVER use external date libraries
 *
 * ENFORCEMENT: ESLint rules prevent usage of forbidden patterns.
 */

export const SingaporeTime = {
  TIMEZONE: 'Asia/Singapore' as const,
  OFFSET: '+08:00' as const,

  /**
   * Get current Singapore timestamp for database storage
   * @returns ISO string with Singapore offset (e.g., "2025-01-11T14:30:00+08:00")
   */
  now(): string {
    const now = new Date();
    const sgTimeString = now
      .toLocaleString('en-CA', {
        timeZone: this.TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
      .replace(', ', 'T');

    return sgTimeString + this.OFFSET;
  },

  /**
   * Convert any date to Singapore timestamp for database storage
   * @param date - Date object or string to convert
   * @returns ISO string with Singapore offset
   */
  timestamp(date: Date | string): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const sgTimeString = dateObj
      .toLocaleString('en-CA', {
        timeZone: this.TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
      .replace(', ', 'T');

    return sgTimeString + this.OFFSET;
  },

  /**
   * Get date-only string in YYYY-MM-DD format (Singapore timezone)
   * @param date - Date to format (defaults to current Singapore time)
   * @returns Date string in YYYY-MM-DD format
   */
  dateOnly(date?: Date | string): string {
    // If no date provided, use current Singapore time
    if (!date) {
      return new Date()
        .toLocaleString('en-CA', {
          timeZone: this.TIMEZONE,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
        .split(',')[0];
    }

    // If it's already a Date object, use it directly
    if (date instanceof Date) {
      return date
        .toLocaleString('en-CA', {
          timeZone: this.TIMEZONE,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
        .split(',')[0];
    }

    // For string dates, parse more carefully to avoid timezone issues
    const dateStr = String(date).trim();

    // If it's already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    // If it's an ISO timestamp format, extract date portion and convert to Singapore timezone
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateStr)) {
      try {
        // Create a Date object from the ISO string
        const dateObj = new Date(dateStr);

        // Convert to Singapore timezone and extract date portion
        return dateObj
          .toLocaleString('en-CA', {
            timeZone: this.TIMEZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
          .split(',')[0];
      } catch (error) {
        // If parsing fails, continue to other methods
        // Intentional console.warn: unified-date is a leaf utility; importing
        // Logger here would create a circular dependency (Logger imports this module).
        console.warn(`[SingaporeTime.dateOnly] Failed to parse ISO timestamp: "${dateStr}"`);
      }
    }

    // For other date formats (like M/D/YYYY from Excel), parse as local noon time
    // to avoid timezone shifting issues
    const parts = dateStr.split(/[/\-.]/);
    let year: number, month: number, day: number;

    if (parts.length === 3) {
      // Check if it's YYYY-MM-DD or YYYY/MM/DD format
      if (parts[0].length === 4) {
        year = parseInt(parts[0]);
        month = parseInt(parts[1]);
        day = parseInt(parts[2]);
      }
      // Check if it's MM/DD/YYYY or MM-DD-YYYY format (US format)
      else if (parts[2].length === 4) {
        month = parseInt(parts[0]);
        day = parseInt(parts[1]);
        year = parseInt(parts[2]);
      }
      // Assume DD/MM/YYYY format
      else {
        day = parseInt(parts[0]);
        month = parseInt(parts[1]);
        year = parseInt(parts[2]);
      }

      // Validate the parsed values
      if (
        !isNaN(year) &&
        !isNaN(month) &&
        !isNaN(day) &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= 31
      ) {
        // Format as YYYY-MM-DD
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }

    // Fallback: Return today's date if we can't parse it
    // We should NOT use Date constructor here as it causes timezone issues
    // Log warning for debugging
    // Intentional console.warn: see note above re: circular dependency.
    console.warn(`[SingaporeTime.dateOnly] Could not parse date: "${dateStr}", using today's date`);

    // Return today's date
    return this.dateOnly();
  },

  /**
   * Extract date portion from a timestamp string
   * @param timestamp - Timestamp string (e.g., "2025-01-11T14:30:00+08:00" or "2025-10-09 11:34")
   * @returns Date portion as YYYY-MM-DD in Singapore timezone
   */
  extractDate(timestamp: string): string {
    const trimmed = timestamp.trim();

    // If it contains a space, split and return the date part (handles "YYYY-MM-DD HH:MM" format)
    if (trimmed.includes(' ')) {
      const datePart = trimmed.split(' ')[0];
      // Validate it's a proper date format
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return datePart;
      }
    }

    // If it contains 'T', use dateOnly to extract (handles ISO format)
    if (trimmed.includes('T')) {
      return this.dateOnly(trimmed);
    }

    // If it's already a date-only string, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    // Fallback: try to parse and convert to Singapore date
    return this.dateOnly(trimmed);
  },

  /**
   * Extract time portion from a timestamp string
   * @param timestamp - Timestamp string
   * @returns Time portion as HH:MM
   */
  extractTime(timestamp: string): string {
    // Convert to Date object and then extract Singapore time
    const dateObj = new Date(timestamp);
    return dateObj.toLocaleString('en-US', {
      timeZone: this.TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  },

  /**
   * Create a date range for database queries (Singapore timezone)
   * @param date - Date in YYYY-MM-DD format
   * @returns Object with start and end timestamps for the day
   */
  dateRange(date: string): { start: string; end: string } {
    const nextDayStr = this.addDays(date, 1);

    return {
      start: `${date}T00:00:00${this.OFFSET}`,
      end: `${nextDayStr}T00:00:00${this.OFFSET}`,
    };
  },

  /**
   * Check if a date is today in Singapore timezone
   * @param date - Date to check
   * @returns True if the date is today
   */
  isToday(date: Date | string): boolean {
    const dateStr = typeof date === 'string' ? this.extractDate(date) : this.dateOnly(date);
    const todayStr = this.dateOnly();

    return dateStr === todayStr;
  },

  /**
   * Check if two dates are the same day in Singapore timezone
   * @param date1 - First date
   * @param date2 - Second date
   * @returns True if both dates are on the same day
   */
  isSameDate(date1: Date | string, date2: Date | string): boolean {
    const date1Str = typeof date1 === 'string' ? this.extractDate(date1) : this.dateOnly(date1);
    const date2Str = typeof date2 === 'string' ? this.extractDate(date2) : this.dateOnly(date2);

    return date1Str === date2Str;
  },

  /**
   * Format a date for display with various options
   */
  format(
    date: Date | string,
    options: {
      showTime?: boolean;
      showToday?: boolean;
      use12Hour?: boolean;
      relative?: boolean;
    } = {}
  ): string {
    const { showTime = true, showToday = false, use12Hour = false, relative = false } = options;

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Handle relative time (e.g., "2 hours ago")
    if (relative) {
      return this.getRelativeTime(dateObj);
    }

    // Check if it's today
    if (this.isToday(dateObj)) {
      if (!showToday) {
        // Just show time for today
        if (typeof date === 'string' && date.includes('T')) {
          return this.extractTime(date);
        }
        return dateObj.toLocaleString('en-US', {
          timeZone: this.TIMEZONE,
          hour: '2-digit',
          minute: '2-digit',
          hour12: use12Hour,
        });
      } else if (showTime) {
        // Show "Today HH:MM"
        const timeStr = dateObj.toLocaleString('en-US', {
          timeZone: this.TIMEZONE,
          hour: '2-digit',
          minute: '2-digit',
          hour12: use12Hour,
        });
        return `Today ${timeStr}`;
      } else {
        return 'Today';
      }
    }

    // Check if it's yesterday
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (this.isSameDate(dateObj, yesterday)) {
      if (showToday && showTime) {
        const timeStr = dateObj.toLocaleString('en-US', {
          timeZone: this.TIMEZONE,
          hour: '2-digit',
          minute: '2-digit',
          hour12: use12Hour,
        });
        return `Yesterday ${timeStr}`;
      } else if (showToday) {
        return 'Yesterday';
      }
    }

    // For other dates, show date with optional time
    if (!showTime) {
      return this.dateOnly(dateObj);
    }

    // Full date and time in YYYY-MM-DD HH:MM format
    const dateStr = this.dateOnly(dateObj);
    const timeStr = dateObj.toLocaleString('en-US', {
      timeZone: this.TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: use12Hour,
    });

    return `${dateStr} ${timeStr}`;
  },

  /**
   * Get relative time string (e.g., "2 hours ago")
   */
  getRelativeTime(date: Date | string): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 60) {
      return 'just now';
    } else if (diffMin < 60) {
      return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    } else if (diffHr < 24) {
      return `${diffHr} hour${diffHr !== 1 ? 's' : ''} ago`;
    } else if (diffDay < 7) {
      return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
    } else {
      return this.dateOnly(dateObj);
    }
  },

  /**
   * Add or subtract days from a date
   */
  addDays(date: Date | string, days: number): string {
    // Plain YYYY-MM-DD: use pure calendar math so the result never depends on the
    // runtime's local timezone.
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [y, m, d] = date.split('-').map(Number);
      const shifted = new Date(Date.UTC(y, m - 1, d + days));
      const mm = String(shifted.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(shifted.getUTCDate()).padStart(2, '0');
      return `${shifted.getUTCFullYear()}-${mm}-${dd}`;
    }

    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const result = new Date(dateObj);
    result.setDate(result.getDate() + days);
    return this.dateOnly(result);
  },

  /**
   * Get the start of the current month in Singapore timezone
   */
  monthStart(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDay = new Date(year, month, 1);
    return this.timestamp(firstDay);
  },

  /**
   * Get the end of the current month in Singapore timezone
   */
  monthEnd(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const lastDay = new Date(year, month + 1, 0);
    return this.timestamp(lastDay);
  },

  /**
   * Get the first day of the current month (date only)
   */
  getFirstDayOfMonth(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDay = new Date(year, month, 1);
    return this.dateOnly(firstDay);
  },

  /**
   * Get the last day of the current month (date only)
   */
  getLastDayOfMonth(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const lastDay = new Date(year, month + 1, 0);
    return this.dateOnly(lastDay);
  },

  // ================================
  // ENHANCED UTILITY METHODS
  // ================================

  /**
   * Get week boundaries (Monday to Sunday) for a given date
   */
  getWeekBoundaries(date?: DateInput): WeekBoundaries {
    const inputDate = date ? (typeof date === 'string' ? new Date(date) : date) : new Date();
    const day = inputDate.getDay();
    const diff = inputDate.getDate() - day + (day === 0 ? -6 : 1); // Get Monday
    const monday = new Date(inputDate);
    monday.setDate(diff);

    const dates: SingaporeDateString[] = [];
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(monday);
      currentDay.setDate(monday.getDate() + i);
      dates.push(this.dateOnly(currentDay) as SingaporeDateString);
    }

    return {
      start: dates[0],
      end: dates[6],
      dates,
    };
  },

  /**
   * Get month boundaries with both date strings and timestamps
   */
  getMonthBoundaries(date?: DateInput): MonthBoundaries {
    const inputDate = date ? (typeof date === 'string' ? new Date(date) : date) : new Date();
    const year = inputDate.getFullYear();
    const month = inputDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const start = this.dateOnly(firstDay) as SingaporeDateString;
    const end = this.dateOnly(lastDay) as SingaporeDateString;

    return {
      start,
      end,
      startTimestamp: `${start}T00:00:00${this.OFFSET}` as SingaporeTimestampString,
      endTimestamp: `${end}T23:59:59${this.OFFSET}` as SingaporeTimestampString,
    };
  },

  /**
   * Create timestamp for specific time on a date (noon by default to avoid timezone issues)
   */
  createTimestamp(
    date: SingaporeDateString,
    hour: number = 12,
    minute: number = 0,
    second: number = 0
  ): SingaporeTimestampString {
    const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
    return `${date}T${timeStr}${this.OFFSET}` as SingaporeTimestampString;
  },

  /**
   * Get start of day timestamp (00:00:00)
   */
  startOfDay(date: DateInput): SingaporeTimestampString {
    const dateStr =
      typeof date === 'string' && this.isValidSingaporeDate(date) ? date : this.dateOnly(date);
    return `${dateStr}T00:00:00${this.OFFSET}` as SingaporeTimestampString;
  },

  /**
   * Get end of day timestamp (23:59:59)
   */
  endOfDay(date: DateInput): SingaporeTimestampString {
    const dateStr =
      typeof date === 'string' && this.isValidSingaporeDate(date) ? date : this.dateOnly(date);
    return `${dateStr}T23:59:59${this.OFFSET}` as SingaporeTimestampString;
  },

  /**
   * Parse Excel date serial number to Singapore date
   */
  fromExcelDate(serial: number): SingaporeDateString {
    // Excel date serial number: days since January 1, 1900
    const excelEpoch = new Date(1900, 0, 1);
    const date = new Date(excelEpoch.getTime() + (serial - 1) * 24 * 60 * 60 * 1000);
    return this.dateOnly(date) as SingaporeDateString;
  },

  /**
   * Short date for compact UI rows: 'D MMM' in Singapore time (e.g. '29 Sep').
   */
  formatShortDate(date: DateInput): string {
    // Full timestamps carry their own offset — parse as-is so UTC instants
    // near the SGT boundary land on the correct SGT day. Bare YYYY-MM-DD
    // strings get a noon-SGT anchor (a date alone has no time to shift).
    const dateObj =
      typeof date === 'string'
        ? date.includes('T')
          ? new Date(date)
          : new Date(`${date}T12:00:00+08:00`)
        : date;
    // en-SG spells September 'Sept'; truncate to the common 3-letter form.
    return new Intl.DateTimeFormat('en-SG', {
      day: 'numeric',
      month: 'short',
      timeZone: this.TIMEZONE,
    })
      .format(dateObj)
      .replace('Sept', 'Sep');
  },

  /**
   * Long date for headings: 'Weekday D Month YYYY' in Singapore time
   * (e.g. 'Tuesday 20 October 2026').
   */
  formatLongDate(date: DateInput): string {
    // Same anchor rule as formatShortDate: a bare YYYY-MM-DD string has no
    // time to shift, so pin it at noon SGT; full timestamps carry their own
    // offset.
    const dateObj =
      typeof date === 'string'
        ? date.includes('T')
          ? new Date(date)
          : new Date(`${date}T12:00:00+08:00`)
        : date;
    // en-SG inserts a comma after the weekday; headings want the plain form.
    return new Intl.DateTimeFormat('en-SG', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: this.TIMEZONE,
    })
      .format(dateObj)
      .replace(/,/g, '');
  },

  /**
   * Day of week for a date (0=Sunday..6=Saturday), resolved in the Singapore
   * timezone. Accepts 'YYYY-MM-DD' or any parseable date string. Noon is used
   * as the reference time so a UTC-offset host cannot shift the calendar day.
   */
  getDayOfWeek(date: DateInput): number {
    const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dateObj =
      typeof date === 'string'
        ? date.includes('T')
          ? new Date(date)
          : new Date(`${date}T12:00:00+08:00`)
        : date;
    const name = dateObj.toLocaleString('en-US', { timeZone: this.TIMEZONE, weekday: 'long' });
    return Math.max(0, names.indexOf(name));
  },

  /**
   * Compare two dates (returns -1, 0, or 1)
   */
  compareDates(date1: DateInput, date2: DateInput): number {
    const d1 = this.dateOnly(date1);
    const d2 = this.dateOnly(date2);

    if (d1 < d2) return -1;
    if (d1 > d2) return 1;
    return 0;
  },

  /**
   * Check if a date is between two other dates (inclusive)
   */
  isBetween(date: DateInput, start: DateInput, end: DateInput): boolean {
    const dateStr = this.dateOnly(date);
    const startStr = this.dateOnly(start);
    const endStr = this.dateOnly(end);

    return dateStr >= startStr && dateStr <= endStr;
  },

  /**
   * Get array of dates between two dates (inclusive)
   */
  getDatesBetween(start: DateInput, end: DateInput): SingaporeDateString[] {
    const startStr = this.dateOnly(start);
    const endStr = this.dateOnly(end);
    const dates: SingaporeDateString[] = [];

    const current = new Date(startStr);
    const endDate = new Date(endStr);

    while (current <= endDate) {
      dates.push(this.dateOnly(current) as SingaporeDateString);
      current.setDate(current.getDate() + 1);
    }

    return dates;
  },

  /**
   * Type-safe helper to check if string is valid Singapore date
   */
  isValidSingaporeDate(date: string): date is SingaporeDateString {
    return /^\d{4}-\d{2}-\d{2}$/.test(date);
  },

  /**
   * Type-safe helper to check if string is valid Singapore timestamp
   */
  isValidSingaporeTimestamp(timestamp: string): timestamp is SingaporeTimestampString {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+08:00$/.test(timestamp);
  },

  /**
   * Convert timestamp to milliseconds for time calculations
   * Use this instead of new Date().getTime() for duration calculations
   */
  toMilliseconds(timestamp: string): number {
    return new Date(timestamp).getTime();
  },

  /**
   * Calculate duration between two timestamps in hours
   */
  calculateDurationHours(startTimestamp: string, endTimestamp: string): number {
    const start = this.toMilliseconds(startTimestamp);
    const end = this.toMilliseconds(endTimestamp);
    return (end - start) / (1000 * 60 * 60);
  },

  /**
   * Create timestamp from date and time strings
   */
  fromDateAndTime(date: SingaporeDateString, time: string): SingaporeTimestampString {
    return `${date}T${time}${this.OFFSET}` as SingaporeTimestampString;
  },
};

// ================================
// TYPESCRIPT TYPES FOR ENFORCEMENT
// ================================

/** Singapore date string in YYYY-MM-DD format */
export type SingaporeDateString = `${number}-${number}-${number}`;

/** Singapore timestamp string with timezone offset */
export type SingaporeTimestampString =
  `${number}-${number}-${number}T${number}:${number}:${number}+08:00`;

/** Time string in HH:MM format */
export type TimeString = `${number}:${number}`;

/** Valid date input types for SingaporeTime methods */
export type DateInput = Date | string | SingaporeDateString | SingaporeTimestampString;

/** Format options for date display */
export interface DateFormatOptions {
  showTime?: boolean;
  showToday?: boolean;
  use12Hour?: boolean;
  relative?: boolean;
}

/** Date range for database queries */
export interface DateRange {
  start: SingaporeTimestampString;
  end: SingaporeTimestampString;
}

/** Month boundaries */
export interface MonthBoundaries {
  start: SingaporeDateString;
  end: SingaporeDateString;
  startTimestamp: SingaporeTimestampString;
  endTimestamp: SingaporeTimestampString;
}

/** Week boundaries */
export interface WeekBoundaries {
  start: SingaporeDateString;
  end: SingaporeDateString;
  dates: SingaporeDateString[];
}

// ================================
// VALIDATION UTILITIES
// ================================

// ================================
// FORBIDDEN USAGE DETECTION
// ================================

/**
 * Runtime check for forbidden Date usage
 * This should be called in development to catch violations
 */
export const detectForbiddenDateUsage = () => {
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    const originalDate = window.Date;

    // Override Date constructor to warn about usage
    window.Date = function (...args: any[]) {
      // Check call stack to avoid warnings from within the unified-date system
      const stack = new Error().stack || '';
      const isFromUnifiedDate =
        stack.includes('unified-date.ts') ||
        stack.includes('unified-date.js') ||
        stack.includes('logger.ts') ||
        stack.includes('logger.js') ||
        stack.includes('dateOnly');

      if (!isFromUnifiedDate) {
        console.warn(
          '🚨 FORBIDDEN: Direct Date constructor usage detected!\n' +
            'Use SingaporeTime methods instead:\n' +
            '• SingaporeTime.now() for current time\n' +
            '• SingaporeTime.dateOnly() for date strings\n' +
            '• SingaporeTime.timestamp() for conversions'
        );
      }

      return new (originalDate as any)(...args);
    } as any;

    // Copy static methods
    Object.setPrototypeOf(window.Date, originalDate);
    Object.defineProperty(window.Date, 'prototype', {
      value: originalDate.prototype,
      writable: false,
    });
  }
};
